import { t } from '@hefe/i18n';
import {
  AssignableType,
  AssignableTypeEnum,
  SelectOptionGroup,
  Task,
  TaskableType,
  TaskableTypeEnum,
  TaskAssignment,
  TaskEvent,
  TaskEventType,
} from '@hefe/models';
import { TasksApi, userActiveBasketballCoachTeamId } from '@hefe/store';
import { colors } from '@hefe/ui';
import { message, Select, Space, Tooltip } from 'antd';
import { useMemo, useState } from 'react';
import { FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import InlineEditInput from '../../../core/components/inline-edit-input/inline-edit-input.component';

interface TaskAssignmentSelectComponentProps {
  task: Task;
  taskId?: string;
  assignments: TaskAssignment[];
  assignableType: AssignableTypeEnum;
  event: TaskEvent;
  type: TaskableTypeEnum;
  teamId?: number; // if assignment should not be active coach team id
  disabled?: boolean;
  checkPosition?: 'front' | 'back';
}

export default function TaskAssignmentSelectComponent(
  props: TaskAssignmentSelectComponentProps
) {
  const [didFetchSelects, setDidFetchSelects] = useState(false);

  const [
    fetchPossibleAssignnees,
    { data: possibleAssignees, isLoading: isLoadingPossibleAssignees },
  ] = TasksApi.useLazyFetchPossibleTaskAssigneesQuery();

  const [assignToTask, { isLoading }] = TasksApi.useAssignToTaskMutation();

  const teamId = useSelector(userActiveBasketballCoachTeamId);

  const fetchData = () => {
    if (didFetchSelects) {
      return;
    }

    const eventId = props.event.id.split('_')[1] as unknown as number;
    const type =
      props.event.id.split('_')[0] === '0'
        ? TaskEventType.BASKETBALL_GAME
        : TaskEventType.BASKETBALL_PRACTICE;
    const taskId = props.taskId?.split('_')[1] as unknown as number;

    fetchPossibleAssignnees({
      request: {
        game_id: type === TaskEventType.BASKETBALL_GAME ? eventId : undefined,
        practice_id:
          type === TaskEventType.BASKETBALL_PRACTICE ? eventId : undefined,
        only_basketball_teams:
          props.assignableType === AssignableType.BASKETBALL_TEAM,
        only_users: props.assignableType === AssignableType.USER,
      },
      taskId: taskId,
    });

    setDidFetchSelects(true);
  };

  const status = useMemo(() => {
    const userAssignmentsCount = props.assignments?.filter(
      (assignment) => assignment.assignable_type === AssignableType.USER
    ).length;
    const currentNumberOfAssignments =
      props.event.num_of_user_assignments ?? userAssignmentsCount ?? 0;
    const minNumOfUsers = props.task.min_num_of_users ?? 0;

    if (props.assignableType === AssignableType.BASKETBALL_TEAM) {
      return null;
    }

    if (currentNumberOfAssignments < minNumOfUsers) {
      return (
        <Tooltip
          title={t('errors.tasks.min_number_of_users', {
            min: minNumOfUsers,
          })}
        >
          <FaExclamationTriangle color={colors.orange} />
        </Tooltip>
      );
    }

    return <FaCheckCircle color={colors.green} />;

    return null;
  }, [
    props.assignableType,
    props.assignments,
    props.event.num_of_user_assignments,
    props.task.min_num_of_users,
  ]);

  const onSave = async (values: string[]) => {
    const newNumberOfAssignments =
      (props.event.num_of_user_assignments ?? 0) -
      props.assignments.length +
      values.length;
    const maxNumOfUsers = props.task?.max_num_of_users;

    // check if newNumberOfAssignments is higher than task max

    if (maxNumOfUsers && newNumberOfAssignments > maxNumOfUsers) {
      message.error(t('errors.tasks.max_number_of_users'));
      return;
    }

    const idParts = props.event.id.split('_');
    const taskIdParts = props.taskId?.split('_');
    if (!props.taskId || idParts.length !== 2 || taskIdParts?.length !== 2) {
      return;
    }

    const taskId = parseInt(taskIdParts[1]);

    const gameId = idParts[0] === '0' ? parseInt(idParts[1]) : undefined;
    const practiceId = idParts[0] === '1' ? parseInt(idParts[1]) : undefined;

    const userIds = values
      .filter((id) => id.startsWith('u_'))
      .map((id) => parseInt(id.replace('u_', '')));
    const basketballTeamIds = values
      .filter((id) => id.startsWith('t_'))
      .map((id) => parseInt(id.replace('t_', '')));

    let tId = undefined;
    if (props.type === TaskableType.BASKETBALL_TEAM) {
      tId = props.teamId ?? teamId;
      if (!tId) {
        return;
      }
    }

    await assignToTask({
      request: {
        game_id: gameId,
        practice_id: practiceId,
        user_ids: userIds,
        basketball_team_ids: basketballTeamIds,
        basketball_team_id: tId,
        keep_basketball_teams: props.assignableType === AssignableType.USER,
        keep_users: props.assignableType === AssignableType.BASKETBALL_TEAM,
      },
      taskId: taskId,
    });
  };

  const assignments = useMemo(() => {
    return props.assignments.filter((assignment) => {
      switch (props.assignableType) {
        case AssignableType.ALL:
          return true;
        case AssignableType.BASKETBALL_TEAM:
          return assignment.assignable_type === AssignableType.BASKETBALL_TEAM;
        case AssignableType.USER:
          return assignment.assignable_type === AssignableType.USER;
        default:
          return true;
      }
    });
  }, [props.assignableType, props.assignments]);

  const ids = useMemo(() => {
    return assignments
      .filter((assignment) => {
        const assignmentPrefix =
          assignment.assignable_type === AssignableType.USER ? 'u_' : 't_';
        for (const group of possibleAssignees ?? []) {
          // check if group.options has assignment.assignable_id
          if (
            group.options?.find(
              (searchOption) =>
                searchOption.value ===
                `${assignmentPrefix}${assignment.assignable_id}`
            )
          ) {
            return true;
          }
        }

        return false;
      })
      .map((assignment) => {
        const prefix =
          assignment.assignable_type === TaskableType.BASKETBALL_TEAM
            ? 't_'
            : 'u_';
        return `${prefix}${assignment.assignable_id}`;
      });
  }, [assignments, possibleAssignees]);

  const text = useMemo(() => {
    if (assignments.length > 0) {
      return assignments
        .map((assignment) => assignment.select_option?.label)
        .join(', ');
    }
    return t('pending');
  }, [assignments]);

  return (
    <Space direction="horizontal">
      {props.checkPosition !== 'back' && status}
      <InlineEditInput
        highlightingMode="parent"
        component={Select}
        onModeChange={(edit) => {
          if (edit) {
            fetchData();
          }
        }}
        value={isLoadingPossibleAssignees ? [] : ids}
        text={text}
        loading={isLoadingPossibleAssignees || isLoading}
        disabled={props.disabled}
        onOk={(value: string[]) => onSave(value)}
        componentProps={{
          options: possibleAssignees,
          mode: 'multiple',
          showSearch: true,
          optionFilterProp: 'children',
          filterOption: (input: string, option: SelectOptionGroup) => {
            const isGroup = Array.isArray(option.options);
            if (isGroup) {
              return false;
            }
            return option.label.toLowerCase().includes(input.toLowerCase());
          },
        }}
      />
      {props.checkPosition === 'back' && status}
    </Space>
  );
}
